import { belowBreakpoint, Box, Heading, Icon, IconSize, Text, Theme, useMediaQuery } from '@bechtel/gds';
import {
  CommodityMilestone,
  CrewMember,
  MaterialCommodity,
  MilestoneStatusCode,
  OwnershipLevel,
  PersonaType,
  Scalars,
  Weld,
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { filter, get, map } from 'lodash';
import React, { FC, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { GET_DASHBOARD_STATS_POST_CLAIM } from '@bechtel/client-gql';
import { bankersRound, useName, useAppUser, PersonaMessages } from '@bechtel/client-common';
import { WorkListDetailButton } from '~/worklist-detail-button';
import { WorkListMenuType } from '~/worklist-detail-button/types';
import { LabelStyled } from '~/worklist/item/styled';
import {
  CommodityContext,
  ApproveAction,
  RejectAction,
  UnapproveAction,
  UnclaimAction,
} from '@bechtel/client-work-packages/src/detail/commodities';
import { WorkPackageContext } from '@bechtel/client-work-packages/src/detail';
import { AvatarRegularStyled, CrewMemberStyled, PendingApprovalListItemStyled, WorklistInfoStyled } from './styled';

interface PendingApprovalListItemProps {
  item: CommodityMilestone;
  isOutOfPlan: Scalars['Boolean'];
  isPendingTabSelected: Scalars['Boolean'];
  crewMember: CrewMember | null | undefined;
  weekStartDate: Scalars['Date'];
  weekEndDate: Scalars['Date'];
  onWorkListMenuSelect: (workListMenuType: WorkListMenuType) => void;
  onActionCompleted: () => void;
  disableAction?: boolean;
  onActionUpdating?: () => void;
}

export interface WorkListInfoProps {
  item: CommodityMilestone;
  commodity: MaterialCommodity | Weld;
  isOutOfPlan: Scalars['Boolean'];
}

const ApprovedMilestone = () => {
  return (
    <LabelStyled status={MilestoneStatusCode.APPROVED}>
      <Box mr={{ sm: '300' }}>
        <Icon icon="check" size={IconSize.MEDIUM} fillColor="blue.800" />
      </Box>
      <Text size={'300'} color={'grey.1000'}>
        <Trans>Approved</Trans>
      </Text>
    </LabelStyled>
  );
};

export const WorkListInfo: FC<WorkListInfoProps> = ({ item, commodity, isOutOfPlan }) => {
  return (
    <WorklistInfoStyled data-testid="worklist-info">
      <Heading size="400">
        {item.name} - {bankersRound(item.hours, 0)} <Trans>hours</Trans>
      </Heading>
      <Text color="grey.600" size="300">
        {commodity?.type.name} : {commodity?.tag}
      </Text>
      <Text color="grey.600" size="300">
        {commodity?.workPackage.tag}
      </Text>
      {isOutOfPlan && (
        <Text color="red.700" size="300" weight="600">
          <Trans>Out of Plan</Trans>
        </Text>
      )}
    </WorklistInfoStyled>
  );
};

export const PendingApprovalListItem: FC<PendingApprovalListItemProps> = ({
  item,
  isOutOfPlan,
  isPendingTabSelected,
  crewMember,
  weekStartDate,
  weekEndDate,
  onWorkListMenuSelect,
  onActionCompleted,
  onActionUpdating,
  disableAction,
}) => {
  const { user } = useAppUser();
  const { getFullName } = useName();
  const { breakpoints, colors } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const createdByUserId = get(item, 'currentStatus.createdBy.id');

  //@ts-ignore ignore undefined type warning as commodity wrapper will either have a weld or material commodity always
  const commodity: MaterialCommodity | Weld = item.commodity.materialCommodity
    ? item.commodity.materialCommodity
    : item.commodity.weld;

  const crewMembers = filter(
    commodity?.workPackage?.primaryOwners,
    (owner) => owner.persona === PersonaType.FOREMAN || owner.persona === PersonaType.GENERAL_FOREMAN
  );

  const approveRefetchQueries = [
    {
      query: GET_DASHBOARD_STATS_POST_CLAIM,
      variables: {
        input: {
          crewMemberId: crewMember?.id,
          personaType: crewMember?.persona,
          ownershipLevel: OwnershipLevel.PRIMARY,
          startDate: weekStartDate,
          endDate: weekEndDate,
        },
      },
    },
    'pendingApprovalSummary',
  ];

  return (
    <PendingApprovalListItemStyled data-testid="pending-approval-item">
      <WorkListInfo item={item} commodity={commodity} isOutOfPlan={isOutOfPlan} />
      <CrewMemberStyled data-testid="pending-approval-owners">
        {map(crewMembers, ({ crewMember, persona }) => {
          return (
            <Box key={`${item.id}-${persona}`} alignItems={{ sm: 'center' }}>
              <AvatarRegularStyled src={crewMember?.imageUrl ? crewMember.imageUrl : undefined} />
              <Box flexDirection={{ sm: 'column' }}>
                <Box mb={{ sm: '300' }}>
                  <Text size={isWideScreen ? '200' : '300'} color="grey.1000" weight="600">
                    {crewMember?.firstName ? getFullName(crewMember?.firstName, crewMember?.lastName) : t`Unassigned`}
                  </Text>
                </Box>
                <Text size="200" color="grey.600">
                  <Trans id={PersonaMessages[persona].id}></Trans>
                  {crewMember?.crew?.crewNumber ? ` - ${crewMember?.crew?.crewNumber}` : ''}
                </Text>
              </Box>
            </Box>
          );
        })}
      </CrewMemberStyled>
      <WorkPackageContext id={commodity.workPackage?.id} tag={commodity.workPackage.tag}>
        <CommodityContext
          id={commodity.id}
          tag={commodity.tag}
          typeId={commodity.type.id}
          typeName={commodity.type.name}
        >
          {isPendingTabSelected && (
            <Box
              data-testid="pending-approval-list-actions"
              alignSelf={{ sm: 'center' }}
              flexDirection={{ sm: 'column' }}
              style={{ width: '35%' }}
            >
              <Box mb={{ sm: '400' }}>
                <Box style={{ width: '50%' }} mr={{ sm: '400' }}>
                  {user?.id === item.currentStatus.createdBy?.id ? (
                    <UnclaimAction
                      milestone={item}
                      status={item.currentStatus?.codeI18n}
                      refetchQueries={approveRefetchQueries}
                      customStyle={{
                        marginRight: '0px',
                        backgroundColor: String(colors.white),
                        borderColor: String(colors.yellow[300]),
                      }}
                      onActionUpdating={onActionUpdating}
                      onActionCompleted={onActionCompleted}
                      disabled={disableAction}
                    ></UnclaimAction>
                  ) : (
                    <RejectAction
                      milestone={item}
                      status={item.currentStatus?.codeI18n}
                      refetchQueries={approveRefetchQueries}
                      customStyle={{
                        marginRight: '0px',
                        backgroundColor: String(colors.white),
                        borderColor: String(colors.yellow[300]),
                      }}
                      onActionCompleted={onActionCompleted}
                      disabled={disableAction}
                    ></RejectAction>
                  )}
                </Box>
                <Box style={{ width: '50%' }}>
                  <ApproveAction
                    milestone={item}
                    status={item.currentStatus?.codeI18n}
                    refetchQueries={approveRefetchQueries}
                    customStyle={{ backgroundColor: String(colors.yellow[300]) }}
                    onActionUpdating={onActionUpdating}
                    onActionCompleted={onActionCompleted}
                    disabled={disableAction}
                  ></ApproveAction>
                </Box>
              </Box>
              <WorkListDetailButton milestone={item} isPendingList={true} onWorkListMenuSelect={onWorkListMenuSelect} />
            </Box>
          )}
          {!isPendingTabSelected && (
            <Box data-testid="approved-list-actions" style={{ width: '35%' }} alignItems={{ sm: 'center' }}>
              <WorkListDetailButton milestone={item} onWorkListMenuSelect={onWorkListMenuSelect} />
              <Box ml={{ sm: '400' }} style={{ width: '50%' }}>
                {user?.id === createdByUserId ? (
                  <UnapproveAction
                    milestone={item}
                    status={item.currentStatus?.codeI18n}
                    refetchQueries={approveRefetchQueries}
                    customStyle={{ backgroundColor: String(colors.yellow[300]) }}
                    onActionUpdating={onActionUpdating}
                    onActionCompleted={onActionCompleted}
                    disabled={disableAction}
                  ></UnapproveAction>
                ) : (
                  <ApprovedMilestone></ApprovedMilestone>
                )}
              </Box>
            </Box>
          )}
        </CommodityContext>
      </WorkPackageContext>
    </PendingApprovalListItemStyled>
  );
};
